import { getDefaultPropfind, getRootPath, resultToNode } from '@nextcloud/files/dav';
import { join } from 'path';
import { useFilesStore } from '../store/files.ts';
import { getPinia } from '../store/index.ts';
import { useSearchStore } from '../store/search.ts';
import { logger } from '../utils/logger.ts';
import { client } from './WebdavClient.ts';
import { searchNodes } from './WebDavSearch.ts';
/**
 * Get contents implementation for the files view.
 * This also allows to fetch local search results when the user is currently filtering.
 *
 * @param path - The path to query
 * @param options - Options
 * @param options.signal - Abort signal to cancel the request
 */
export async function getContents(path = '/', options) {
    const searchStore = useSearchStore(getPinia());
    if (searchStore.query.length < 3) {
        return await defaultGetContents(path, options);
    }
    return await getLocalSearch(path, searchStore.query, options?.signal);
}
/**
 * Generic `getContents` implementation for the users files.
 *
 * @param path - The path to get the contents
 * @param options - Options
 * @param options.signal - Abort signal to cancel the request
 */
export async function defaultGetContents(path, options) {
    path = join(getRootPath(), path);
    const propfindPayload = getDefaultPropfind();
    const contentsResponse = await client.getDirectoryContents(path, {
        details: true,
        data: propfindPayload,
        includeSelf: true,
        signal: options?.signal,
    });
    const root = contentsResponse.data[0];
    const contents = contentsResponse.data.slice(1);
    if (root?.filename !== path && `${root?.filename}/` !== path) {
        logger.debug(`Exepected "${path}" but got filename "${root.filename}" instead.`);
        throw new Error('Root node does not match requested path');
    }
    return {
        folder: resultToNode(root),
        contents: contents.map((result) => {
            try {
                return resultToNode(result);
            }
            catch (error) {
                logger.error(`Invalid node detected '${result.basename}'`, { error });
                return null;
            }
        }).filter(Boolean),
    };
}
/**
 * Get the local search results for the current folder.
 *
 * @param path - The path
 * @param query - The current search query
 * @param signal - The aboort signal
 */
async function getLocalSearch(path, query, signal) {
    const filesStore = useFilesStore(getPinia());
    let folder = filesStore.getDirectoryByPath('files', path);
    if (!folder) {
        const rootPath = join(getRootPath(), path);
        const stat = await client.stat(rootPath, { details: true });
        folder = resultToNode(stat.data);
    }
    const contents = await searchNodes(query, { dir: path, signal });
    return {
        folder,
        contents,
    };
}
